import { useEffect } from "react";

interface SeoMetaOptions {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  author?: string;
  publishedTime?: string;
  section?: string;
  keywords?: string;
}

const SITE_NAME = "Wazi Stories Afrique";
const DEFAULT_IMAGE = "https://public.readdy.ai/ai/img_res/447714ac0d897b672e89d7cdd686ae15.jpg";

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? "name" : "property";
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeoMeta({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  author,
  publishedTime,
  section,
  keywords,
}: SeoMetaOptions) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = url || window.location.href;
    const truncatedDesc = description.length > 160 ? description.slice(0, 157) + "..." : description;

    // Page title
    document.title = fullTitle;

    // Standard meta
    setMeta("description", truncatedDesc, true);
    if (keywords) setMeta("keywords", keywords, true);
    if (author) setMeta("author", author, true);
    setMeta("last-modified", new Date().toISOString(), true);

    // Canonical
    setLink("canonical", canonicalUrl);

    // Open Graph
    setMeta("og:title", fullTitle);
    setMeta("og:description", truncatedDesc);
    setMeta("og:image", image);
    setMeta("og:image:width", "1200");
    setMeta("og:image:height", "630");
    setMeta("og:url", canonicalUrl);
    setMeta("og:type", type);
    setMeta("og:site_name", SITE_NAME);
    setMeta("og:locale", "fr_FR");

    // Article-specific OG
    if (type === "article") {
      if (publishedTime) setMeta("article:published_time", publishedTime);
      if (author) setMeta("article:author", author);
      if (section) setMeta("article:section", section);
    }

    // Twitter Card
    setMeta("twitter:card", "summary_large_image", true);
    setMeta("twitter:title", fullTitle, true);
    setMeta("twitter:description", truncatedDesc, true);
    setMeta("twitter:image", image, true);
    setMeta("twitter:site", "@WaziStoriesAfrique", true);

    // Cleanup on unmount — restore defaults
    return () => {
      document.title = SITE_NAME;
    };
  }, [title, description, image, url, type, author, publishedTime, section, keywords]);
}
